import { render } from 'preact';
import { initA11yWidget, BRAND } from '@vibeshell/shared';
import './styles.css';
import './platform.css';
// §V2 exact port — the spike's design system, scoped under [data-v2].
import './v2.css';
// Build provenance — Vite inlines __BUILD_SHA__ (the VITE_GIT_SHA passed at
// build time) so the live bundle carries its exact commit. Exposed on window
// and a <meta> so HEAD==live can be verified from the browser or a curl.
declare const __BUILD_SHA__: string;
try {
  (window as unknown as { __pomelaBuild?: string }).__pomelaBuild = __BUILD_SHA__;
  const m = document.createElement('meta');
  m.name = 'pomela-build';
  m.content = __BUILD_SHA__;
  document.head.appendChild(m);
} catch {
  /* no-op */
}
import { App } from './App';
import { CreatorRoot } from './CreatorRoot';
import { ErrorBoundary } from './components/ErrorBoundary';
// Phase 8 — Maintenance gate decision logic lives in
// `lib/maintenance-gate.ts` as `shouldShowMaintenance(defaultOn)`. It
// combines the build-time feature flag (`VITE_MAINTENANCE_MODE`) with
// runtime bypass affordances (?dev=1, localStorage flag set by the
// 5-tap wordmark, localhost). The original Phase 5 inline check has
// been retired because it had no runtime bypass — the gate became
// truly impossible to escape once `.env.production` baked the flag to
// `true`. See lib/maintenance-gate.ts for the full precedence order.
import { MaintenanceGate } from './components/MaintenanceGate';
import { passIfSignedIn } from './lib/maintenance-gate';
import { shouldShowMaintenance } from './lib/maintenance-gate';
import { initAnalytics } from './lib/analytics';
import { initDeviceId } from './lib/deviceId';
import { initBrowserSentry, exposeSentryTestHook } from './lib/sentry';
import { applyTheme, getThemePref } from './lib/theme';
import { purgeDuplicates } from './lib/recent-apps';
import { initLocale } from './lib/i18n';
import { captureCreatorReferral } from './lib/referral';

// ─── Bulletproof boot sequence ──────────────────────────────────────────────
//
// EVERY synchronous boot helper runs inside `safeBoot()`. A single throw from
// any of them would otherwise prevent the boot splash from being removed,
// leaving the user permanently stuck on the pink-progress-bar loading screen.
// All failures degrade gracefully — the user gets the app even if analytics,
// device-id persistence, or theme detection fails.
//
// Plus a hard 5-second safety timeout that force-removes the splash. If
// React mount itself somehow hangs, the user still sees SOMETHING — at
// worst an empty page they can reload — rather than a forever-loading
// screen with no exit.

function safeBoot(label: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[boot] ${label} failed, continuing boot`, err);
  }
}

function removeBootSplash(): void {
  try {
    document.getElementById('boot-splash')?.remove();
  } catch {
    /* paranoid — DOM removal should never throw, but boot must continue */
  }
}

// Hard safety net — if React mount silently hangs for any reason, force
// the splash off after 5s. Cleared the moment the splash is removed
// cleanly below.
const splashSafetyTimeout = setTimeout(() => {
  // eslint-disable-next-line no-console
  console.warn('[boot] safety timeout fired, force-removing splash');
  removeBootSplash();
}, 5000);

// §WS14B — detect the system locale + set <html lang dir> (RTL-aware) BEFORE
// first paint, so a Hebrew/Arabic visitor gets a correctly-mirrored shell from
// frame one (no LTR flash).
safeBoot('initLocale', () => initLocale());
safeBoot('initDeviceId', () => initDeviceId());
safeBoot('initAnalytics', () => initAnalytics());
// §H — capture ?ref (badge taps + invite links) BEFORE the maintenance
// gate renders, so waitlist-phase badge clicks are attributed too.
safeBoot('captureCreatorReferral', () => captureCreatorReferral());
// Brutal workspace dedup BEFORE any UI mounts — guarantees the
// localStorage payload is free of duplicate appIds even if a previous
// build wrote them, so Workspace and Sidebar both render a clean list.
// purgeDuplicates() already has its own outer try/catch but we wrap it
// again so any future change to it can't bring down the boot.
safeBoot('purgeDuplicates', () => purgeDuplicates());
// Apply the saved light/dark/system preference before first paint.
safeBoot('applyTheme', () => applyTheme(getThemePref()));

// Idempotent. No-op if VITE_SENTRY_DSN is unset or @sentry/browser missing.
safeBoot('initBrowserSentry', () => {
  void initBrowserSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE as string | undefined
  });
  exposeSentryTestHook();
});

safeBoot('serviceWorker', () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* private browsing or blocked — degrade gracefully */
    });
    // Self-heal — when a NEW service worker takes control mid-session,
    // reload exactly once so the next paint uses its fresh fetch
    // strategy (e.g. the v3 SW that stops caching Vite dev paths).
    // Guarded by a sessionStorage flag so we never reload-loop if some
    // SW edge case keeps firing `controllerchange` repeatedly.
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      // Only reload if there's actually a controller change AFTER the
      // page already had one — first-install fires this too, and we
      // don't want to reload on a brand-new visit.
      if (sessionStorage.getItem('vs:sw-handoff-seen') === '1') {
        window.location.reload();
      } else {
        sessionStorage.setItem('vs:sw-handoff-seen', '1');
      }
    });
  }
});

// iOS keyboard handling: when the on-screen keyboard appears, visualViewport
// shrinks below window.innerHeight. We mirror that delta into a CSS custom
// property so layouts (e.g. PromptInput) can reserve space above the keyboard.
safeBoot('keyboardInset', () => {
  if (typeof window !== 'undefined' && window.visualViewport) {
    const update = () => {
      try {
        const inset = Math.max(0, window.innerHeight - window.visualViewport!.height);
        document.documentElement.style.setProperty('--keyboard-inset', `${inset}px`);
      } catch {
        /* a runtime style failure must not block keyboard handling */
      }
    };
    window.visualViewport.addEventListener('resize', update);
    window.visualViewport.addEventListener('scroll', update);
    update();
  }
});

// ─── Mount React tree ───────────────────────────────────────────────────────
//
// `render()` itself can throw if a top-level component constructor (the
// ones outside the ErrorBoundary) throws synchronously during the first
// render. Wrapping the whole mount in try/catch makes sure the splash
// still comes down even in that case — the user then sees either the
// rendered app, or a minimal failure fallback we paint into #root.

const root = document.getElementById('root');
if (root) {
  try {
    // ─── Phase 8 — Gate decision with runtime bypass support ───────────
    //
    // The build-time `VITE_MAINTENANCE_MODE` flag is the DEFAULT. The
    // helper inspects four runtime escape hatches before deferring to
    // it (?gate=1 force-on, ?dev=1 force-off, localStorage bypass,
    // localhost). See lib/maintenance-gate.ts for full precedence.
    //
    // Bundle implication: a runtime decision keeps the App branch
    // reachable, so the creator stays in the bundle alongside the gate.
    // This is the conscious trade-off for restoring the founder
    // bypass — Phase 6's dead-code-elimination win is gone, but the
    // gate is no longer a one-way door.
    const isMaintenance = shouldShowMaintenance(
      import.meta.env.VITE_MAINTENANCE_MODE === 'true'
    );

    if (isMaintenance) {
      render(
        <ErrorBoundary>
          <MaintenanceGate />
        </ErrorBoundary>,
        root
      );
      // §SIGNED-IN-IS-NOT-A-WAITLIST-CANDIDATE — measured in production: a creator with an account,
      // on the allowlist, holding a valid session was shown "Get early access", because this page
      // never asked the API who the visitor was. The gate renders FIRST (no flash for the logged-out
      // visitor, who is nearly everyone) and the answer arrives behind it; a signed-in creator is
      // then let through to their workspace. Fails closed on any error.
      void passIfSignedIn((import.meta.env.VITE_API_BASE as string | undefined) ?? '');
    } else if (location.pathname.startsWith('/admin')) {
      // The admin analytics dashboard lives under /admin. It (and recharts)
      // are dynamically imported so they form a separate chunk — the main
      // creator bundle never pays for the dashboard's weight.
      import('./screens/admin/AdminApp')
        .then(({ AdminApp }) =>
          render(
            <ErrorBoundary>
              <AdminApp />
            </ErrorBoundary>,
            root
          )
        )
        .catch(() =>
          render(
            <ErrorBoundary>
              <App />
            </ErrorBoundary>,
            root
          )
        );
    } else {
      render(
        <ErrorBoundary>
          <CreatorRoot />
        </ErrorBoundary>,
        root
      );
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[boot] React mount failed', err);
    // Minimal, dependency-free fallback so the user sees SOMETHING they
    // can act on rather than the infinite loading splash.
    try {
      // Crash fallback — same canonical brand mark as the favicon +
      // boot splash, so even the error state stays on-brand. Inline
      // <style> (rather than onmouse* handlers) so press feedback
      // works on touch devices too — the old mouse-only handlers
      // were a no-op on iOS, the platform this product targets
      // (NIGHT_AUDIT §2.6).
      root.innerHTML = `
        <style>
          .pomela-crash-reload {
            margin-top: 4px;
            padding: 11px 22px;
            border-radius: 11px;
            border: none;
            background: #f4477e;
            color: #fff;
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
            touch-action: manipulation;
            transition: transform 120ms cubic-bezier(0.34, 1.34, 0.64, 1),
                        background 120ms cubic-bezier(0.4, 0, 0.2, 1);
          }
          .pomela-crash-reload:hover { background: #e13a6f; }
          .pomela-crash-reload:active { transform: scale(0.96); background: #c92f5e; }
          .pomela-crash-reload:focus-visible {
            outline: 2px solid #f4477e;
            outline-offset: 3px;
          }
        </style>
        <div style="min-height:100dvh;display:grid;place-items:center;padding:32px;background:#0a0a0a;color:#fafafa;font-family:'Inter',system-ui,sans-serif;text-align:center">
          <div style="max-width:380px;display:flex;flex-direction:column;align-items:center;gap:18px">
            <svg width="64" height="64" viewBox="0 0 512 512" role="img" aria-label="Pomela" xmlns="http://www.w3.org/2000/svg" style="border-radius:16px;box-shadow:0 12px 32px -10px rgba(244,71,126,0.55)">
              <defs>
                <linearGradient id="cr-tile" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stop-color="#FF9F5A"/>
                  <stop offset="0.52" stop-color="#F4477E"/>
                  <stop offset="1" stop-color="#7C4DFF"/>
                </linearGradient>
                <radialGradient id="cr-sheen" cx="30%" cy="22%" r="78%">
                  <stop offset="0" stop-color="#FFFFFF" stop-opacity="0.38"/>
                  <stop offset="0.62" stop-color="#FFFFFF" stop-opacity="0"/>
                </radialGradient>
                <mask id="cr-pcut">
                  <rect width="512" height="512" fill="#000"/>
                  <rect x="141" y="124" width="52" height="276" rx="26" fill="#fff"/>
                  <circle cx="263" cy="224" r="108" fill="#fff"/>
                  <circle cx="263" cy="224" r="50" fill="#000"/>
                  <circle cx="281" cy="208" r="21" fill="#fff"/>
                </mask>
              </defs>
              <rect width="512" height="512" rx="112" fill="url(#cr-tile)"/>
              <rect width="512" height="512" rx="112" fill="url(#cr-sheen)"/>
              <rect width="512" height="512" fill="#fff" mask="url(#cr-pcut)"/>
            </svg>
            <h1 style="font-size:20px;margin:0;color:#fafafa;font-weight:600;letter-spacing:-0.02em">Pomela couldn't start</h1>
            <p style="font-size:14px;line-height:1.55;color:#a3a3a3;margin:0">Something went wrong while loading the app. Reload to try again, if it persists, clear site data for this domain.</p>
            <button onclick="location.reload()" class="pomela-crash-reload" type="button">Reload</button>
          </div>
        </div>`;
    } catch {
      /* even the fallback failed — nothing more we can do, but the splash
         will still come down via the finally + safety timeout */
    }
  }
}

// The boot splash (index.html) has done its job — the app owns #root now.
// This runs WHETHER OR NOT React mounted successfully, so the splash is
// guaranteed off even if every step above threw.
removeBootSplash();
clearTimeout(splashSafetyTimeout);

// §G-A11Y-OPT — the IS 5568 accessibility option + statement on the platform itself.
// Mounts independently of React (its own DOM), so it survives even a failed mount.
try {
  initA11yWidget({ contactEmail: BRAND.supportEmail, siteName: BRAND.name });
} catch {
  /* the widget is best-effort; never let it block the app */
}
